/**
 * PhoneField — input de teléfono con selector de prefijo de país + validación.
 *
 * Componente CONTROLADO sobre el string en **E.164** (`+34600100200`), que es lo
 * que el formulario guarda en `phone`/`whatsapp`. Internamente mantiene el país
 * y el número nacional, y emite el E.164 por `onChange`. Preselecciona España.
 *
 * Sirve tanto para React Hook Form (vía `Controller`) como para formularios con
 * `useState`. Dos variantes visuales: `light` (fondo claro) y `dark` (landings
 * dark). La validación real la hace `isValidLandingPhone` (libphonenumber-js).
 */
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChevronDown, Check, Search } from "lucide-react";
import type { CountryCode } from "libphonenumber-js";
import {
  COUNTRY_OPTIONS,
  getCountryOption,
  parseE164,
  toE164,
} from "@/lib/landing/phone";

export interface PhoneFieldProps {
  /** Valor en E.164 (lo que guarda el formulario). */
  value: string;
  onChange: (e164: string) => void;
  error?: string;
  variant?: "light" | "dark";
  /** id del input numérico (para `<label htmlFor>` y tests). */
  inputId?: string;
  placeholder?: string;
  autoComplete?: string;
  disabled?: boolean;
}

function cx(...xs: Array<string | false | undefined>): string {
  return xs.filter(Boolean).join(" ");
}

export function PhoneField({
  value,
  onChange,
  error,
  variant = "light",
  inputId,
  placeholder = "600 100 200",
  autoComplete = "tel",
  disabled = false,
}: PhoneFieldProps) {
  const autoId = useId();
  const numberId = inputId ?? `phone-${autoId}`;
  const dark = variant === "dark";

  const initial = useMemo(() => parseE164(value), []); // solo en el montaje
  const [country, setCountry] = useState<CountryCode>(initial.iso);
  const [national, setNational] = useState<string>(initial.national);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Sincroniza si el valor externo cambia a algo que no acabamos de emitir
  // (p. ej. `reset()` del formulario, o un valor inicial async).
  useEffect(() => {
    if ((value || "") !== toE164(country, national)) {
      const p = parseE164(value);
      setCountry(p.iso);
      setNational(p.national);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Cerrar el dropdown al click fuera o Escape; enfocar el buscador al abrir.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    const tid = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(tid);
    };
  }, [open]);

  function emit(nextIso: CountryCode, nextNational: string) {
    onChange(toE164(nextIso, nextNational));
  }
  function handleNational(e: React.ChangeEvent<HTMLInputElement>) {
    const v = e.target.value.replace(/[^\d\s]/g, "");
    setNational(v);
    emit(country, v);
  }
  function selectCountry(iso: CountryCode) {
    setCountry(iso);
    setOpen(false);
    setQuery("");
    emit(iso, national);
  }

  const selected = getCountryOption(country);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRY_OPTIONS;
    const qDigits = q.replace(/^\+/, "");
    return COUNTRY_OPTIONS.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.iso.toLowerCase().includes(q) ||
        c.dialCode.includes(qDigits),
    );
  }, [query]);

  // Tokens de color por variante (layout va por Tailwind).
  const field = dark
    ? { background: "rgba(255,255,255,0.06)", border: `1px solid rgba(126,217,87,0.35)`, color: "#FFFFFF" }
    : { background: "#FFFFFF", border: "1px solid #e5e7eb", color: "#111827" };
  const panel = dark
    ? { background: "#0B0F0D", border: "1px solid rgba(126,217,87,0.35)", color: "#FFFFFF" }
    : { background: "#FFFFFF", border: "1px solid #e5e7eb", color: "#111827" };
  const accent = dark ? "#7ED957" : "#16a34a";

  return (
    <div ref={rootRef} className="relative">
      <div className="flex items-stretch gap-2">
        {/* Box de prefijo */}
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`Prefijo de país: ${selected.name} (+${selected.dialCode})`}
          className="flex h-11 shrink-0 items-center gap-1.5 rounded-lg px-3 text-base outline-none focus:ring-2"
          style={field}
        >
          <span className="text-lg leading-none" aria-hidden="true">{selected.flag}</span>
          <span className="font-medium tabular-nums">+{selected.dialCode}</span>
          <ChevronDown className="h-4 w-4 opacity-70" aria-hidden="true" />
        </button>

        {/* Número nacional */}
        <input
          id={numberId}
          disabled={disabled}
          type="tel"
          inputMode="tel"
          autoComplete={autoComplete}
          value={national}
          onChange={handleNational}
          placeholder={placeholder}
          aria-invalid={error ? true : undefined}
          className="h-11 w-full rounded-lg px-4 text-base outline-none focus:ring-2"
          style={field}
        />
      </div>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 z-50 mt-1 w-72 max-w-[90vw] overflow-hidden rounded-lg shadow-xl"
          style={panel}
        >
          <div className="flex items-center gap-2 border-b px-3 py-2" style={{ borderColor: dark ? "rgba(126,217,87,0.2)" : "#f0f0f0" }}>
            <Search className="h-4 w-4 opacity-60" aria-hidden="true" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar país…"
              className="w-full bg-transparent text-sm outline-none"
              style={{ color: panel.color }}
            />
          </div>
          <ul className="max-h-64 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm opacity-60">Sin resultados</li>
            )}
            {filtered.map((c) => (
              <li key={c.iso}>
                <button
                  type="button"
                  role="option"
                  aria-selected={c.iso === country}
                  onClick={() => selectCountry(c.iso)}
                  className={cx(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm",
                    dark ? "hover:bg-white/10" : "hover:bg-gray-100",
                  )}
                  style={{ color: panel.color }}
                >
                  <span className="text-base leading-none" aria-hidden="true">{c.flag}</span>
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="tabular-nums opacity-70">+{c.dialCode}</span>
                  {c.iso === country && (
                    <Check className="h-4 w-4" style={{ color: accent }} aria-hidden="true" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <p className="mt-1 text-sm" style={{ color: dark ? "#ff6b6b" : "#ef4444" }} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default PhoneField;
